/**
 * ReferenceDataReview.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-01: Reference data review
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/ReferenceDataReview.jsx
 *
 * S89 audit patches: PATCH-01, PATCH-22 — see original header for details.
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE = '/api/v1';

const ENTITY_CONFIG = {
  businessUnits: { key: 'businessUnits', label: 'Business Units', endpoint: '/business-units', idField: 'business_unit_id', editableFields: ['bu_code','bu_name','bu_description','bu_weight','quota_currency_code','is_active','sort_order'], columns: [{ field:'bu_code',label:'BU Code'},{ field:'bu_name',label:'BU Name'},{ field:'bu_description',label:'Description'},{ field:'bu_weight',label:'Weight'},{ field:'quota_currency_code',label:'Currency'},{ field:'is_active',label:'Active'},{ field:'sort_order',label:'Sort'}] },
  segments: { key: 'segments', label: 'Segments', endpoint: '/segments', idField: 'segment_id', editableFields: ['segment_code','segment_name','description','is_active','sort_order'], columns: [{ field:'segment_code',label:'Segment Code'},{ field:'segment_name',label:'Segment Name'},{ field:'description',label:'Description'},{ field:'is_active',label:'Active'},{ field:'sort_order',label:'Sort'}] },
  roles: { key: 'roles', label: 'Roles', endpoint: '/roles', idField: 'role_id', editableFields: ['role_code','role_name','role_description','is_active','sort_order'], columns: [{ field:'role_code',label:'Role Code'},{ field:'role_name',label:'Role Name'},{ field:'role_description',label:'Description'},{ field:'role_category',label:'Category',readOnly:true},{ field:'is_active',label:'Active'},{ field:'sort_order',label:'Sort'}] },
  sellingMotions: { key: 'sellingMotions', label: 'Selling Motions', endpoint: '/selling-motions', idField: 'selling_motion_id', editableFields: ['motion_code','motion_name','motion_description','allows_overlay','credit_split_pct','is_active','sort_order'], columns: [{ field:'motion_code',label:'Motion Code'},{ field:'motion_name',label:'Motion Name'},{ field:'motion_description',label:'Description'},{ field:'allows_overlay',label:'Overlay'},{ field:'credit_split_pct',label:'Credit Split %'},{ field:'is_active',label:'Active'},{ field:'sort_order',label:'Sort'}] }
};

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) { return error?.message || 'Something went wrong.'; }

function EditableCell({ value, readOnly, onChange }) {
  if (readOnly) return <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">{String(value ?? '')}</span>;
  if (typeof value === 'boolean') {
    return (
      <select value={value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value === 'true')} className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm">
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  return <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm" />;
}

export default function ReferenceDataReview({ tenantId, tier, importLogId, onContinue }) {
  const { token } = useAuth();

  const [activeTab, setActiveTab] = useState('businessUnits');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [segments, setSegments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sellingMotions, setSellingMotions] = useState([]);
  const [geoNodes, setGeoNodes] = useState([]);
  const [editMap, setEditMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [importWarnings, setImportWarnings] = useState([]);
  const [loadErrors, setLoadErrors] = useState({});
  const [loadingTabs, setLoadingTabs] = useState({});

  const tabs = useMemo(() => {
    const baseTabs = Object.values(ENTITY_CONFIG);
    if (tier === 'enterprise') return [...baseTabs, { key: 'geoHierarchy', label: 'Geo Hierarchy', endpoint: null, idField: 'id', columns: [] }];
    return baseTabs;
  }, [tier]);

  const setEntityState = useCallback((key, updaterOrRows) => {
    const applyUpdate = (setter) => setter(updaterOrRows);
    if (key === 'businessUnits') applyUpdate(setBusinessUnits);
    if (key === 'segments') applyUpdate(setSegments);
    if (key === 'roles') applyUpdate(setRoles);
    if (key === 'sellingMotions') applyUpdate(setSellingMotions);
  }, []);

  const entityRows = useMemo(() => ({ businessUnits, segments, roles, sellingMotions, geoHierarchy: geoNodes }), [businessUnits, segments, roles, sellingMotions, geoNodes]);

  const fetchEntity = useCallback(async (config) => {
    if (!config.endpoint) { setGeoNodes([]); return; }
    setLoadingTabs((c) => ({ ...c, [config.key]: true }));
    setLoadErrors((c) => ({ ...c, [config.key]: null }));
    try {
      const response = await fetch(`${API_BASE}${config.endpoint}`, { credentials: 'include' });
      if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload?.error?.message || `Failed to load ${config.label}`); }
      const payload = await response.json();
      setEntityState(config.key, normalizeResponse(payload));
    } catch (error) { setLoadErrors((c) => ({ ...c, [config.key]: getErrorMessage(error) })); }
    finally { setLoadingTabs((c) => ({ ...c, [config.key]: false })); }
  }, [setEntityState, token]);

  const fetchImportWarnings = useCallback(async () => {
    if (!importLogId) return;
    try {
      const response = await fetch(`${API_BASE}/import-log/${importLogId}`, { credentials: 'include' });
      if (!response.ok) return;
      const payload = await response.json();
      const warnings = payload?.warnings || payload?.importWarnings || [];
      setImportWarnings(Array.isArray(warnings) ? warnings : []);
    } catch (_error) { setImportWarnings([]); }
  }, [importLogId, token]);

  useEffect(() => { Object.values(ENTITY_CONFIG).forEach((config) => fetchEntity(config)); fetchImportWarnings(); }, [fetchEntity, fetchImportWarnings]);

  const activeConfig = useMemo(() => tabs.find((tab) => tab.key === activeTab) || ENTITY_CONFIG.businessUnits, [activeTab, tabs]);

  const updateCell = (config, row, field, value) => {
    const editKey = `${config.key}:${row[config.idField]}`;
    setEditMap((c) => ({ ...c, [editKey]: { ...(c[editKey] || {}), [field]: value } }));
  };

  const getRowValue = (config, row, field) => {
    const editKey = `${config.key}:${row[config.idField]}`;
    if (Object.prototype.hasOwnProperty.call(editMap[editKey] || {}, field)) return editMap[editKey][field];
    return row[field];
  };

  const saveRow = async (config, row) => {
    if (!config.endpoint) return;
    const rowId = row[config.idField];
    const editKey = `${config.key}:${rowId}`;
    const changes = editMap[editKey] || {};
    if (Object.keys(changes).length === 0) return;
    setIsSaving(true); setSaveError(null);
    try {
      const response = await fetch(`${API_BASE}${config.endpoint}/${rowId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changes) });
      if (!response.ok) { const payload = await response.json().catch(() => ({})); throw new Error(payload?.error?.message || 'Save failed'); }
      const savedPayload = await response.json().catch(() => null);
      const savedRow = savedPayload?.data || savedPayload || { ...row, ...changes };
      if (config.key === 'businessUnits') setBusinessUnits((c) => c.map((e) => e[config.idField] === rowId ? { ...e, ...savedRow } : e));
      else if (config.key === 'segments') setSegments((c) => c.map((e) => e[config.idField] === rowId ? { ...e, ...savedRow } : e));
      else if (config.key === 'roles') setRoles((c) => c.map((e) => e[config.idField] === rowId ? { ...e, ...savedRow } : e));
      else if (config.key === 'sellingMotions') setSellingMotions((c) => c.map((e) => e[config.idField] === rowId ? { ...e, ...savedRow } : e));
      setEditMap((c) => { const next = { ...c }; delete next[editKey]; return next; });
    } catch (error) { setSaveError({ rowId, message: getErrorMessage(error) }); }
    finally { setIsSaving(false); }
  };

  const rows = entityRows[activeConfig.key] || [];
  const hasPendingEdits = Object.keys(editMap).length > 0;

  return (
    <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-01</p>
        <h2 className="mt-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">Reference data review</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">Review imported business units, segments, roles, and selling motions before continuing.</p>
      </div>

      {importWarnings.length > 0 && (
        <div className="m-5 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-4 text-tok-sm text-[rgb(var(--color-warning))]">
          <p className="font-semibold">Import warnings</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {importWarnings.map((warning, index) => <li key={`${warning?.code || 'warning'}-${index}`}>{warning?.message || String(warning)}</li>)}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-[rgb(var(--color-border))] px-5 pt-4">
        {tabs.map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
            className={`rounded-t-xl px-4 py-2 text-tok-sm font-medium ${activeTab === tab.key ? 'bg-[rgb(var(--color-primary))] text-white' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-border))]'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-5">
        {loadErrors[activeConfig.key] && (
          <div className="mb-4 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load {activeConfig.label}</p>
            <p className="mt-1">{loadErrors[activeConfig.key]}</p>
            <button type="button" onClick={() => fetchEntity(activeConfig)} className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white">Retry</button>
          </div>
        )}

        {activeConfig.key === 'geoHierarchy' ? (
          <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] p-6 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Enterprise geo hierarchy review is reserved for M031/M032 region and sub-region endpoints.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead className="bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  {activeConfig.columns.map((column) => (
                    <th key={column.field} className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">{column.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold text-[rgb(var(--color-text))]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                {loadingTabs[activeConfig.key] ? (
                  <tr><td colSpan={activeConfig.columns.length + 1} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">Loading {activeConfig.label}...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={activeConfig.columns.length + 1} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">No rows found.</td></tr>
                ) : (
                  rows.map((row) => {
                    const rowId = row[activeConfig.idField];
                    const editKey = `${activeConfig.key}:${rowId}`;
                    const hasRowEdits = Boolean(editMap[editKey]);
                    const rowError = saveError?.rowId === rowId ? saveError.message : null;
                    return (
                      <tr key={rowId} className={rowError ? 'bg-[rgb(var(--color-danger))]/5' : ''}>
                        {activeConfig.columns.map((column) => (
                          <td key={column.field} className="min-w-[160px] px-4 py-3 align-top text-[rgb(var(--color-text))]">
                            <EditableCell value={getRowValue(activeConfig, row, column.field)} readOnly={column.readOnly} onChange={(value) => updateCell(activeConfig, row, column.field, value)} />
                            {column.field === 'role_category' && <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Engine-constrained</p>}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right align-top text-[rgb(var(--color-text))]">
                          <button type="button" disabled={!hasRowEdits || isSaving} onClick={() => saveRow(activeConfig, row)} className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))]">
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                          {rowError && <p className="mt-2 text-tok-xs text-[rgb(var(--color-danger))]">{rowError}</p>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          {hasPendingEdits ? 'You have unsaved edits. Save changed rows before continuing.' : 'Review is optional. You may continue when ready.'}
        </p>
        <button type="button" onClick={onContinue} className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">Continue</button>
      </div>
    </section>
  );
}
